import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { OPTIMIZATION_TYPE, Task } from '@bringg/types';
import { UserOptimizationData } from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import { areTasksInSameDay } from '@bringg-frontend/shared-tasks-date';
import { getDropOffWayPoint } from '@bringg-frontend/utils';

import notification from 'bringg-web/services/notification';
import { useStores } from 'bringg-web/recipes';
import RoutesPlannerModal from 'bringg-web/components/routes-planner-modal/routes-planner-modal';

interface ModalParams {
	selectedTasks: Task[];
	onApplyChanges?: (userChanges: UserOptimizationData) => void;
	optimizationType: OPTIMIZATION_TYPE;
}

export interface Props {
	onClose: () => void;
	modalParams: ModalParams;
}

const ManualRoutesPlannerOpener = ({ onClose, modalParams }: Props) => {
	const { selectedTasks, optimizationType } = modalParams;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { merchantConfigurationsStore } = useStores();
	const { t } = useTranslation();

	const isOpenFromDispatch = selectedTasks[0].ready_to_execute;

	const getDropOfWayPointsList = (tasks: Task[]) => {
		const dropOffWayPoints = [];
		tasks.forEach(task => {
			const wp = getDropOffWayPoint(task);
			if (wp) {
				dropOffWayPoints.push(wp);
			}
		});
		return dropOffWayPoints;
	};

	const validateOpenRoutesPlannerManually = () => {
		const teamIds = new Set<number>();

		selectedTasks.forEach(task => {
			teamIds.add(task.team_ids[0]);
		});

		if (teamIds.has(undefined)) {
			notification.error(t('PLANNING.ONLY_TASKS_WITH_TEAM'));
			return false;
		}
		if (teamIds.size > 1) {
			notification.error(t('PLANNING.ONLY_ONE_TEAM'));
			return false;
		}

		const tasksDropOffWayPoints = getDropOfWayPointsList(selectedTasks);

		if (
			selectedTasks.length &&
			(tasksDropOffWayPoints.length === 0 || tasksDropOffWayPoints.length !== selectedTasks.length)
		) {
			notification.error(t('PLANNING.TASK_WITHOUT_DROP_OFF_WAY_POINT'));
			return false;
		}

		const tasksWithRun = [];
		const tasksWithNoRun = [];

		selectedTasks.forEach(task => {
			if (task.run_id) {
				tasksWithRun.push(task);
			} else {
				tasksWithNoRun.push(task);
			}
		});

		if (tasksWithNoRun.length && !tasksWithRun.length) {
			notification.error(t('PLANNING.ONLY_TASKS_WITH_RUN'));
			return false;
		}

		if (
			!merchantConfigurationsStore.configuration.allow_multi_day_optimization &&
			!(
				areTasksInSameDay(tasksDropOffWayPoints, 'no_earlier_than', 'scheduled_at') ||
				areTasksInSameDay(tasksDropOffWayPoints, 'scheduled_at', 'no_earlier_than')
			)
		) {
			notification.error(t('PLANNING.ONLY_SAME_DATE'));
			return false;
		}

		return true;
	};

	const getTeamId = () => {
		return selectedTasks[0].team_ids[0];
	};

	useEffect(() => {
		const isOpenRPValid = validateOpenRoutesPlannerManually();
		if (isOpenRPValid) {
			setIsModalOpen(isOpenRPValid);
		} else {
			onClose();
		}
		return () => {
			onClose();
		};
	}, []);

	const onCloseRoutesPlanner = () => {
		onClose();
		setIsModalOpen(false);
	};

	return (
		<>
			{isModalOpen && (
				<RoutesPlannerModal
					onClose={onCloseRoutesPlanner}
					modalParams={{
						optimizationResults: undefined,
						isOpenFromDispatch,
						teamId: getTeamId(),
						selectedTasks,
						optimizationType,
						isManualOptimization: true
					}}
				/>
			)}
		</>
	);
};

export default ManualRoutesPlannerOpener;
